/**
 * Online 101 Okey Oyun Sayfası
 * Oda yönetimi ve oyun arayüzü
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { 
  Users, Clock, Eye, EyeOff, Settings, ArrowLeft, 
  Play, CheckCircle, XCircle, Maximize2, Minimize2,
  Grid3X3, Box, Volume2, VolumeX, Loader2, RotateCcw,
  Layers, ArrowUpDown, Undo2
} from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import api from "../../lib/api";
import OkeyTable2D from "./OkeyTable2D";
import OkeyTable3D from "./OkeyTable3D";
import GameChat from "./GameChat";

export default function OkeyGameRoom() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Oyun durumu
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // UI durumu
  const [viewMode, setViewMode] = useState("3d"); // "2d" veya "3d"
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedTiles, setSelectedTiles] = useState([]);
  const [turnTimeRemaining, setTurnTimeRemaining] = useState(null);
  
  // Taş dizme - local state (backend'e gönderilmeden önce kullanıcı düzenleyebilir)
  const [localTiles, setLocalTiles] = useState([]);
  const [draggedTile, setDraggedTile] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  
  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [isChatMinimized, setIsChatMinimized] = useState(true);
  
  // WebSocket
  const wsRef = useRef(null);
  const timerRef = useRef(null);


  // Oda bilgilerini yükle
  const fetchRoom = useCallback(async (isInitialLoad = false) => {
    try {
      const response = await api.getGameRoom(roomId);
      setRoom(response.data);
      setError(null);
      setRetryCount(0);
      
      // İlk yüklemede otomatik katılma kontrolü
      if (isInitialLoad && response.data) {
        const roomData = response.data;
        const isAlreadyInRoom = roomData.players?.some(p => p.user_id === user?.user_id);
        
        if (!isAlreadyInRoom && roomData.status === "waiting") {
          // Boş koltuk bul ve katıl
          const occupiedSeats = roomData.players?.map(p => p.seat_index) || [];
          const availableSeat = [0, 1, 2, 3].find(s => !occupiedSeats.includes(s));
          
          if (availableSeat !== undefined) {
            try {
              await api.joinGameRoom(roomId, availableSeat);
              // Katıldıktan sonra tekrar yükle
              const updatedRoom = await api.getGameRoom(roomId);
              setRoom(updatedRoom.data);
              toast.success("Odaya katıldınız!");
            } catch (joinErr) {
              const errorMsg = joinErr.response?.data?.detail || "Odaya katılınamadı";
              console.log("Odaya katılma hatası:", errorMsg);
              toast.error(errorMsg);
            }
          }
        }
      }
    } catch (err) {
      // Sadece ilk yüklemede veya çok fazla hata olduysa error göster
      if (isInitialLoad) {
        setError(err.response?.data?.detail || "Oda yüklenemedi");
      } else {
        // Polling sırasında hata - retry count artır
        setRetryCount(prev => {
          if (prev >= 5) {
            setError(err.response?.data?.detail || "Oda bağlantısı kesildi");
          }
          return prev + 1;
        });
      }
    } finally {
      setLoading(false);
    }
  }, [roomId, user?.user_id]);

  const [retryCount, setRetryCount] = useState(0);

  // WebSocket bağlantısı (opsiyonel - fallback olarak polling kullanılır)
  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem("auth_token");
    if (!token) return;

    // Backend API URL'inden WebSocket URL'i oluştur
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const wsProtocol = backendUrl.startsWith('https') ? 'wss:' : 'ws:';
    const wsHost = backendUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
    const wsUrl = `${wsProtocol}//${wsHost}/api/ws/${roomId}`;
    
    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log("Game WebSocket connected");
        ws.send(JSON.stringify({ action: "authenticate", token }));
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === "authenticated") {
          console.log("Authenticated as:", data.role);
        } else if (data.type === "game_update") {
          setRoom(prev => ({
            ...prev,
            ...data,
            game: data.game || data
          }));
          if (data.game) startTurnTimer(data.game);
        } else if (data.type === "player_joined" || data.type === "player_left" || data.type === "player_ready") {
          fetchRoom();
        } else if (data.type === "game_started") {
          setRoom(prev => ({
            ...prev,
            status: "playing",
            game: data.game
          }));
          toast.success("Oyun başladı!");
          if (data.game) startTurnTimer(data.game);
        } else if (data.type === "auto_discard") {
          toast.info("Süre doldu, otomatik taş atıldı");
          fetchRoom();
        } else if (data.type === "chat" || data.type === "reaction") {
          // Chat mesajı veya emoji tepkisi
          setChatMessages(prev => [...prev.slice(-49), data]); // Son 50 mesaj
        } else if (data.type === "system") {
          // Sistem mesajı (oyun olayları)
          setChatMessages(prev => [...prev.slice(-49), data]);
        }
      };

      ws.onerror = (err) => {
        console.log("WebSocket error, using polling instead");
      };

      ws.onclose = () => {
        console.log("Game WebSocket disconnected");
      };

      wsRef.current = ws;
    } catch (err) {
      console.log("WebSocket not available, using polling");
    }
  }, [roomId]);

  // Polling fallback - WebSocket olmadan da oyun durumunu güncelle
  const pollingRef = useRef(null);
  
  const startPolling = useCallback(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    
    pollingRef.current = setInterval(() => {
      if ((room?.status === "playing" || room?.status === "waiting") && !error) {
        fetchRoom(false); // Polling, ilk yükleme değil
      }
    }, 3000); // Her 3 saniyede bir güncelle
  }, [room?.status, error, fetchRoom]);

  // Turn timer
  const startTurnTimer = (gameState) => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    const turnTime = gameState?.turn_time_limit || gameState?.rules?.turn_time || 60;
    const turnStartTime = gameState?.turn_start_time;
    
    if (turnStartTime) {
      // Backend'den gelen başlangıç zamanına göre kalan süreyi hesapla
      const startTime = new Date(turnStartTime).getTime();
      const now = Date.now();
      const elapsed = Math.floor((now - startTime) / 1000);
      const remaining = Math.max(0, turnTime - elapsed);
      setTurnTimeRemaining(remaining);
    } else {
      setTurnTimeRemaining(turnTime);
    }

    timerRef.current = setInterval(() => {
      setTurnTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Oyun aksiyonları
  const handleDrawCenter = async () => {
    try {
      await api.gameAction(roomId, { type: "draw_center" });
      setSelectedTiles([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Taş çekilemedi");
    }
  };

  const handleDrawLeft = async (fromPlayerId) => {
    try {
      await api.gameAction(roomId, { type: "draw_left", from_player_id: fromPlayerId });
      setSelectedTiles([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Taş alınamadı");
    }
  };

  const handleDiscard = async (tileId) => {
    try {
      await api.gameAction(roomId, { type: "discard", tile_id: tileId });
      setSelectedTiles([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Taş atılamadı");
    }
  };

  const handleOpenHand = async (tileIds, openType) => {
    try {
      const pers = [tileIds];
      await api.gameAction(roomId, { type: "open_hand", pers: pers, open_type: openType });
      setSelectedTiles([]);
      toast.success("El açıldı!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "El açılamadı");
    }
  };

  // ===== TAŞ DİZME FONKSİYONLARI =====
  // KURAL: Oyuncu taşların yerleşiminde son karar sahibidir
  // Sistem zorla değiştiremez, boşlukları korur
  
  // GRID YAPISI: 30 slot (2 satır x 15 sütun)
  // Her slot: taş VEYA boşluk (null) barındırabilir
  const TOTAL_SLOTS = 30;
  const SLOTS_PER_ROW = 15;
  
  // Local tiles'ı GRID formatında tut (index => tile veya null)
  // Boşluklar GERÇEK objedir - taş yok ama konulabilir alan
  
  // Local tiles'ı güncelle (backend'den gelen taşlarla sync)
  useEffect(() => {
    const myHand = room?.game?.hands?.[user?.user_id];
    if (myHand?.tiles && localTiles.length === 0) {
      // İlk yüklemede taşları sola dayalı yerleştir ama boşluk bırakmadan
      setLocalTiles(myHand.tiles.map(t => ({ ...t, _gap: false })));
    }
  }, [room?.game?.hands, user?.user_id, localTiles.length]);

  // SÜRÜKLE & BIRAK - Taş taşındığında eski yeri boş kalır
  // Swap yapmaz, sadece taşı yeni yere koyar
  const handleDragStart = (tile, fromIndex) => {
    setDraggedTile({ tile, fromIndex });
  };

  const handleDragOver = (e, toIndex) => {
    e.preventDefault();
    setDragOverIndex(toIndex);
  };

  const handleDragEnd = () => {
    setDraggedTile(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e, toIndex) => {
    e.preventDefault();
    if (!draggedTile) return;
    
    const { tile: sourceTile, fromIndex } = draggedTile;
    
    // Aynı yere bırakılırsa hiçbir şey yapma
    if (fromIndex === toIndex) {
      setDraggedTile(null);
      setDragOverIndex(null);
      return;
    }
    
    // Taşların deep copy'sini al
    const newTiles = localTiles.map(t => ({ ...t }));
    
    // İki taşın yerini swap et (yer değiştir)
    if (toIndex >= 0 && toIndex < newTiles.length && fromIndex >= 0 && fromIndex < newTiles.length) {
      const temp = newTiles[fromIndex];
      newTiles[fromIndex] = newTiles[toIndex];
      newTiles[toIndex] = temp;
    }
    
    setLocalTiles(newTiles);
    setDraggedTile(null);
    setDragOverIndex(null);
  };

  // SERİ DİZ - Seri (run) ve Per (aynı sayı farklı renk) grupları arasında boşluk
  const handleSortBySeries = () => {
    const tiles = [...localTiles];
    const initialCount = tiles.length;
    const colorOrder = { red: 0, blue: 1, black: 2, yellow: 3 };
    
    // Joker'leri ayır
    const jokers = tiles.filter(t => t.color === 'joker');
    const normalTiles = tiles.filter(t => t.color !== 'joker');
    
    // Kullanılan taşları takip et
    const usedTileIds = new Set();
    
    // 1. Seri (run) bul - Aynı renk, ardışık sayılar (en az 3)
    const runs = [];
    
    ['red', 'blue', 'black', 'yellow'].forEach(color => {
      // Bu renkteki tüm taşları al ve sayıya göre sırala
      const colorTiles = normalTiles
        .filter(t => t.color === color && !usedTileIds.has(t.tile_id))
        .sort((a, b) => a.number - b.number);
      
      // Unique sayılara göre grupla (duplicate sayıları atla)
      const uniqueNumbers = [];
      const numberToTile = {};
      
      colorTiles.forEach(tile => {
        if (!numberToTile[tile.number]) {
          numberToTile[tile.number] = tile;
          uniqueNumbers.push(tile.number);
        }
      });
      
      // Ardışık serileri bul
      let currentRun = [];
      uniqueNumbers.forEach(num => {
        if (currentRun.length === 0) {
          currentRun = [numberToTile[num]];
        } else {
          const lastNum = currentRun[currentRun.length - 1].number;
          if (num === lastNum + 1) {
            currentRun.push(numberToTile[num]);
          } else {
            // Seri kırıldı
            if (currentRun.length >= 3) {
              runs.push([...currentRun]);
              currentRun.forEach(t => usedTileIds.add(t.tile_id));
            }
            currentRun = [numberToTile[num]];
          }
        }
      });
      // Son seriyi kontrol et
      if (currentRun.length >= 3) {
        runs.push([...currentRun]);
        currentRun.forEach(t => usedTileIds.add(t.tile_id));
      }
    });
    
    // 2. Per (set) bul - Aynı sayı, farklı renkler (en az 3)
    const pers = [];
    const byNumber = {};
    
    normalTiles.forEach(tile => {
      if (usedTileIds.has(tile.tile_id)) return;
      const key = tile.number;
      if (!byNumber[key]) byNumber[key] = [];
      byNumber[key].push(tile);
    });
    
    // Per oluştur (3+ farklı renk)
    Object.entries(byNumber).forEach(([num, numTiles]) => {
      const uniqueColors = [...new Set(numTiles.map(t => t.color))];
      if (uniqueColors.length >= 3) {
        const perGroup = [];
        const colorsSeen = new Set();
        numTiles.forEach(t => {
          if (!colorsSeen.has(t.color)) {
            perGroup.push(t);
            colorsSeen.add(t.color);
            usedTileIds.add(t.tile_id);
          }
        });
        if (perGroup.length >= 3) {
          pers.push(perGroup);
        }
      }
      // NOT: Per olmayan taşlar (uniqueColors < 3) usedTileIds'e eklenmedi
      // Bu taşlar singles veya pairs olarak işlenecek
    });
    
    // 3. Çift bul (aynı renk + aynı sayı = 2 taş)
    const pairs = [];
    const tileMap = {};
    
    normalTiles.forEach(tile => {
      if (usedTileIds.has(tile.tile_id)) return;
      const key = `${tile.color}_${tile.number}`;
      if (!tileMap[key]) tileMap[key] = [];
      tileMap[key].push(tile);
    });
    
    Object.entries(tileMap).forEach(([key, group]) => {
      if (group.length >= 2) {
        pairs.push([group[0], group[1]]);
        usedTileIds.add(group[0].tile_id);
        usedTileIds.add(group[1].tile_id);
      }
    });
    
    // 4. Kalan tek taşlar
    const singles = normalTiles.filter(t => !usedTileIds.has(t.tile_id))
      .sort((a, b) => {
        const colorDiff = (colorOrder[a.color] || 0) - (colorOrder[b.color] || 0);
        return colorDiff !== 0 ? colorDiff : a.number - b.number;
      });
    
    // 5. Sonuç dizisini oluştur
    const result = [];
    let isFirstGroup = true;
    
    // Seriler
    runs.sort((a, b) => a[0].number - b[0].number);
    runs.forEach(run => {
      run.forEach((tile, i) => {
        result.push({ ...tile, _gap: !isFirstGroup && i === 0 });
      });
      isFirstGroup = false;
    });
    
    // Perler
    pers.sort((a, b) => a[0].number - b[0].number);
    pers.forEach(per => {
      per.sort((a, b) => (colorOrder[a.color] || 0) - (colorOrder[b.color] || 0));
      per.forEach((tile, i) => {
        result.push({ ...tile, _gap: !isFirstGroup && i === 0 });
      });
      isFirstGroup = false;
    });
    
    // Çiftler
    pairs.sort((a, b) => a[0].number - b[0].number);
    pairs.forEach(pair => {
      pair.forEach((tile, i) => {
        result.push({ ...tile, _gap: !isFirstGroup && i === 0 });
      });
      isFirstGroup = false;
    });
    
    // Tek taşlar
    if (singles.length > 0) {
      singles.forEach((tile, i) => {
        result.push({ ...tile, _gap: !isFirstGroup && i === 0 });
      });
      isFirstGroup = false;
    }
    
    // Joker'ler
    jokers.forEach((tile, i) => {
      result.push({ ...tile, _gap: !isFirstGroup && i === 0 });
      isFirstGroup = false;
    });
    
    // Doğrulama
    if (result.length !== initialCount) {
      toast.error(`Hata: ${initialCount - result.length} taş kayboldu!`);
      return;
    }
    
    setLocalTiles(result);
    toast.success(`Seri dizildi! ${runs.length} seri, ${pers.length} per, ${pairs.length} çift bulundu.`);
  };

  // ÇİFT DİZ - Çiftler üst sırada yapışık, tek taşlar altta
  // Örnek üst: 1-1-4-4-6-6-7-7-10-10-BOŞLUK-1-4-5
  // Örnek alt: 2-3-4-BOŞLUK-3-6-8-9
  const handleSortByPairs = () => {
    const tiles = [...localTiles];
    const initialCount = tiles.length;
    const colorOrder = { red: 0, blue: 1, black: 2, yellow: 3 };
    
    // Joker'leri ayır
    const jokers = tiles.filter(t => t.color === 'joker');
    const normalTiles = tiles.filter(t => t.color !== 'joker');
    
    // Çiftleri bul (aynı renk + aynı sayı)
    const tileMap = {};
    normalTiles.forEach(tile => {
      const key = `${tile.color}_${tile.number}`;
      if (!tileMap[key]) tileMap[key] = [];
      tileMap[key].push(tile);
    });
    
    const pairGroups = []; // Her çift bir grup [[t1, t2], [t3, t4], ...]
    const singleTiles = [];
    
    Object.entries(tileMap).forEach(([key, group]) => {
      if (group.length >= 2) {
        pairGroups.push([group[0], group[1]]);
        if (group.length > 2) {
          singleTiles.push(...group.slice(2));
        }
      } else {
        singleTiles.push(...group);
      }
    });
    
    // Çiftleri sayıya göre sırala
    pairGroups.sort((a, b) => a[0].number - b[0].number || 
      (colorOrder[a[0].color] || 0) - (colorOrder[b[0].color] || 0));
    
    // Tek taşları sırala
    singleTiles.sort((a, b) => {
      const colorDiff = (colorOrder[a.color] || 0) - (colorOrder[b.color] || 0);
      return colorDiff !== 0 ? colorDiff : a.number - b.number;
    });
    
    // Sonuç: Çiftler yapışık, sonra boşluk, sonra tekler
    const result = [];
    
    // Çiftler (yapışık - boşluksuz)
    pairGroups.forEach(pair => {
      pair.forEach(tile => {
        result.push({ ...tile, _gap: false });
      });
    });
    
    // Tek taşlar (ilkinin önünde boşluk)
    if (singleTiles.length > 0 && pairGroups.length > 0) {
      result.push({ ...singleTiles[0], _gap: true });
      singleTiles.slice(1).forEach(tile => {
        result.push({ ...tile, _gap: false });
      });
    } else {
      singleTiles.forEach(tile => {
        result.push({ ...tile, _gap: false });
      });
    }
    
    // Joker'ler
    if (jokers.length > 0) {
      result.push({ ...jokers[0], _gap: result.length > 0 });
      jokers.slice(1).forEach(tile => {
        result.push({ ...tile, _gap: false });
      });
    }
    
    // DOĞRULAMA: Taş sayısı değişmemeli!
    if (result.length !== initialCount) {
      console.error(`ÇİFT DİZ HATASI: ${initialCount} taş vardı, ${result.length} taş kaldı!`);
      toast.error(`Hata: Taş sayısı uyuşmuyor!`);
      return; // İşlemi iptal et
    }
    
    setLocalTiles(result);
    toast.success(`Çiftler dizildi! ${pairGroups.length} çift bulundu.`);
  };

  // GERİ AL - Orijinal backend dizilimine dön
  const handleResetTiles = () => {
    const myHand = room?.game?.hands?.[user?.user_id];
    if (myHand?.tiles) {
      setLocalTiles(myHand.tiles.map(t => ({ ...t, _gap: false })));
      toast.info("Taşlar sıfırlandı");
    }
  };

  // DÜZAÇ - Seçili taşlarla seri (run) oluştur
  const handleOpenSeries = () => {
    if (selectedTiles.length < 3) {
      toast.error("En az 3 taş seçmelisiniz!");
      return;
    }
    handleOpenHand(selectedTiles, "seri");  // Backend "seri" bekliyor
  };

  // ÇİFT AÇ - Seçili taşlarla per (set) oluştur
  const handleOpenPairs = () => {
    if (selectedTiles.length < 3) {
      toast.error("En az 3 taş seçmelisiniz!");
      return;
    }
    handleOpenHand(selectedTiles, "duz");  // Backend "duz" bekliyor
  };

  const handleTileSelect = (tileId) => {
    setSelectedTiles(prev => {
      if (prev.includes(tileId)) {
        return prev.filter(id => id !== tileId);
      }
      return [...prev, tileId];
    });
  };

  // ==================== AKILLI YARDIM FONKSIYONLARI ====================
  
  // Eldeki taşlardan geçerli serileri bul
  const findValidSeries = (tiles) => {
    if (!tiles || tiles.length < 3) return [];
    
    const series = [];
    const byColor = {};
    
    // Renklere göre grupla
    tiles.forEach(tile => {
      if (tile.color !== 'joker') {
        if (!byColor[tile.color]) byColor[tile.color] = [];
        byColor[tile.color].push(tile);
      }
    });
    
    // Her renk için ardışık serileri bul
    Object.keys(byColor).forEach(color => {
      const colorTiles = byColor[color].sort((a, b) => a.number - b.number);
      let currentSeries = [colorTiles[0]];
      
      for (let i = 1; i < colorTiles.length; i++) {
        if (colorTiles[i].number === currentSeries[currentSeries.length - 1].number + 1) {
          currentSeries.push(colorTiles[i]);
        } else {
          if (currentSeries.length >= 3) {
            series.push([...currentSeries]);
          }
          currentSeries = [colorTiles[i]];
        }
      }
      if (currentSeries.length >= 3) {
        series.push([...currentSeries]);
      }
    });
    
    return series;
  };
  
  // Eldeki taşlardan geçerli perleri (düz) bul
  const findValidPers = (tiles) => {
    if (!tiles || tiles.length < 3) return [];
    
    const pers = [];
    const byNumber = {};
    
    // Sayılara göre grupla
    tiles.forEach(tile => {
      if (tile.color !== 'joker') {
        if (!byNumber[tile.number]) byNumber[tile.number] = [];
        byNumber[tile.number].push(tile);
      }
    });
    
    // Her sayı için farklı renkleri kontrol et
    Object.keys(byNumber).forEach(number => {
      const numTiles = byNumber[number];
      const uniqueColors = new Set(numTiles.map(t => t.color));
      
      if (uniqueColors.size >= 3) {
        // En az 3 farklı renk varsa per oluşturulabilir
        const perTiles = [];
        const usedColors = new Set();
        numTiles.forEach(tile => {
          if (!usedColors.has(tile.color)) {
            perTiles.push(tile);
            usedColors.add(tile.color);
          }
        });
        if (perTiles.length >= 3) {
          pers.push(perTiles);
        }
      }
    });
    
    return pers;
  };
  
  // Eldeki çiftleri bul
  const findPairs = (tiles) => {
    if (!tiles || tiles.length < 2) return [];
    
    const pairs = [];
    const tileMap = {};
    
    tiles.forEach(tile => {
      if (tile.color !== 'joker') {
        const key = `${tile.color}-${tile.number}`;
        if (!tileMap[key]) tileMap[key] = [];
        tileMap[key].push(tile);
      }
    });
    
    Object.values(tileMap).forEach(group => {
      if (group.length >= 2) {
        pairs.push([group[0], group[1]]);
      }
    });
    
    return pairs;
  };
  
  // Puan hesapla
  const calculatePoints = (tiles) => {
    return tiles.reduce((sum, tile) => {
      if (tile.color === 'joker') return sum;
      return sum + (tile.number || 0);
    }, 0);
  };
  
  // 101 puan açış kontrolü
  const canOpen101 = (tiles) => {
    const series = findValidSeries(tiles);
    const pers = findValidPers(tiles);
    
    // Tüm geçerli kombinasyonların puanını hesapla
    let totalPoints = 0;
    
    series.forEach(s => {
      totalPoints += calculatePoints(s);
    });
    
    pers.forEach(p => {
      totalPoints += calculatePoints(p);
    });
    
    return {
      canOpen: totalPoints >= 101,
      points: totalPoints,
      series: series,
      pers: pers
    };
  };
  
  // 5 çift ile açış kontrolü
  const canOpenWith5Pairs = (tiles) => {
    const pairs = findPairs(tiles);
    return {
      canOpen: pairs.length >= 5,
      pairCount: pairs.length,
      pairs: pairs
    };
  };
  
  // Akıllı yardım - açılabilir mi?
  const smartHelpInfo = (() => {
    const tiles = localTiles?.length > 0 ? localTiles : room?.game?.hands?.[user?.user_id]?.tiles || [];
    const hasOpened = room?.game?.hands?.[user?.user_id]?.is_open;
    
    if (hasOpened) {
      return { type: 'opened', message: 'El açık' };
    }
    
    const open101 = canOpen101(tiles);
    const open5Pairs = canOpenWith5Pairs(tiles);
    
    if (open101.canOpen) {
      return {
        type: 'can_open_101',
        message: `Açabilirsin! (${open101.points} puan)`,
        points: open101.points,
        series: open101.series,
        pers: open101.pers
      };
    }
    
    if (open5Pairs.canOpen) {
      return {
        type: 'can_open_5_pairs',
        message: `5 Çift ile Açabilirsin!`,
        pairs: open5Pairs.pairs
      };
    }
    
    return {
      type: 'cannot_open',
      message: `${open101.points}/101 puan`,
      points: open101.points,
      series: open101.series,
      pers: open101.pers,
      pairCount: open5Pairs.pairCount
    };
  })();
  
  // Seçili taşların geçerli olup olmadığını kontrol et
  const selectedTilesValid = (() => {
    if (selectedTiles.length < 3) return { valid: false, type: null };
    
    const tiles = localTiles?.length > 0 ? localTiles : room?.game?.hands?.[user?.user_id]?.tiles || [];
    const selected = tiles.filter(t => selectedTiles.includes(t.tile_id));
    
    // Seri kontrolü
    if (selected.length >= 3) {
      const colors = new Set(selected.filter(t => t.color !== 'joker').map(t => t.color));
      const numbers = selected.filter(t => t.color !== 'joker').map(t => t.number).sort((a, b) => a - b);
      
      // Aynı renk, ardışık sayılar = seri
      if (colors.size === 1 && numbers.length >= 3) {
        let isConsecutive = true;
        for (let i = 1; i < numbers.length; i++) {
          if (numbers[i] !== numbers[i-1] + 1) {
            isConsecutive = false;
            break;
          }
        }
        if (isConsecutive) {
          return { valid: true, type: 'seri', points: calculatePoints(selected) };
        }
      }
      
      // Farklı renkler, aynı sayı = düz per
      const uniqueNumbers = new Set(numbers);
      if (uniqueNumbers.size === 1 && colors.size === selected.filter(t => t.color !== 'joker').length) {
        return { valid: true, type: 'duz', points: calculatePoints(selected) };
      }
    }
    
    return { valid: false, type: null };
  })();

  // Hazır ol
  const handleReady = async () => {
    try {
      const isReady = !room?.players?.find(p => p.user_id === user?.user_id)?.is_ready;
      await api.setPlayerReady(roomId, isReady);
      fetchRoom();
    } catch (err) {
      toast.error("Hazır durumu değiştirilemedi");
    }
  };

  // Oyunu başlat
  const handleStart = async () => {
    try {
      await api.startGame(roomId);
      toast.success("Oyun başlatılıyor...");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Oyun başlatılamadı");
    }
  };

  // Chat mesajı gönder
  const handleSendChatMessage = (message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        action: "chat",
        message: message
      }));
    }
  };

  // Emoji tepkisi gönder
  const handleSendReaction = (emoji) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        action: "reaction",
        emoji: emoji
      }));
    }
  };

  // Tam ekran
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    fetchRoom();
    connectWebSocket();
    startPolling();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [connectWebSocket, startPolling]);

  // İlk yükleme
  useEffect(() => {
    fetchRoom(true); // İlk yükleme
  }, [roomId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1622] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#4DFFFF]" />
        <span className="text-white ml-3">Oda yükleniyor...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0B1622] flex flex-col items-center justify-center gap-4 p-8">
        <div className="text-center">
          <div className="text-6xl mb-4">🎮</div>
          <h2 className="text-xl font-bold text-white mb-2">Oda Bulunamadı</h2>
          <p className="text-white/60 mb-4">
            {error === "Oda bulunamadı" 
              ? "Bu oyun odası artık mevcut değil. Sunucu yeniden başlatıldığında oyun odaları sıfırlanır."
              : error
            }
          </p>
        </div>
        <Button onClick={() => navigate(-1)} className="bg-[#4DFFFF] text-[#0B1622] hover:bg-[#3CEEEE]">
          Geri Dön
        </Button>
      </div>
    );
  }

  // Oda sahibi veya ilk katılan (koltuk 0) oyun başlatabilir
  const firstPlayer = room?.players?.find(p => p.seat_index === 0);
  const isCreator = room?.created_by === user?.user_id || firstPlayer?.user_id === user?.user_id;
  const minPlayers = 1; // Minimum 1 oyuncu gerekli (test için)
  const canStart = room?.status === "waiting" && 
                   room?.players?.length >= minPlayers && 
                   room?.players?.every(p => p.is_ready);
  const isPlaying = room?.status === "playing";

  return (
    <div className={cn(
      "min-h-screen bg-[#0B1622] flex flex-col",
      isFullscreen && "fixed inset-0 z-50"
    )}>
      {/* Header */}
      <div className="bg-[#050E2E] border-b border-white/10 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-white/10 rounded-lg text-white/70 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-white font-bold">Online 101 Okey</h1>
              <div className="text-white/50 text-sm">Oda: {roomId?.slice(-8)}</div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Görünüm seçici */}
            <div className="flex bg-white/10 rounded-lg p-1">
              <button
                onClick={() => setViewMode("2d")}
                className={cn(
                  "px-3 py-1.5 rounded-md flex items-center gap-2 text-sm transition-colors",
                  viewMode === "2d" ? "bg-[#4DFFFF] text-[#0B1622]" : "text-white/70 hover:text-white"
                )}
              >
                <Grid3X3 className="w-4 h-4" />
                2D
              </button>
              <button
                onClick={() => setViewMode("3d")}
                className={cn(
                  "px-3 py-1.5 rounded-md flex items-center gap-2 text-sm transition-colors",
                  viewMode === "3d" ? "bg-[#4DFFFF] text-[#0B1622]" : "text-white/70 hover:text-white"
                )}
              >
                <Box className="w-4 h-4" />
                3D
              </button>
            </div>

            {/* Tam ekran */}
            <button
              onClick={toggleFullscreen}
              className="p-2 hover:bg-white/10 rounded-lg text-white/70 hover:text-white"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>

            {/* Seyirci sayısı */}
            <div className="flex items-center gap-2 text-white/50">
              <Eye className="w-4 h-4" />
              <span>{room?.spectator_count || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Oyun alanı */}
      <div className="flex-1 relative">
        {isPlaying ? (
          // Oyun görünümü
          viewMode === "2d" ? (
            <OkeyTable2D
              gameState={room?.game || room}
              currentUserId={user?.user_id}
              onDrawCenter={handleDrawCenter}
              onDrawLeft={handleDrawLeft}
              onDiscard={handleDiscard}
              onOpenHand={handleOpenHand}
              selectedTiles={selectedTiles}
              onTileSelect={handleTileSelect}
              turnTimeRemaining={turnTimeRemaining}
              localTiles={localTiles}
              onSortBySeries={handleSortBySeries}
              onSortByPairs={handleSortByPairs}
              onResetTiles={handleResetTiles}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
              dragOverIndex={dragOverIndex}
              onOpenSeries={handleOpenSeries}
              onOpenPairs={handleOpenPairs}
              smartHelpInfo={smartHelpInfo}
              selectedTilesValid={selectedTilesValid}
            />
          ) : (
            <OkeyTable3D
              gameState={room?.game || room}
              currentUserId={user?.user_id}
              onDrawCenter={handleDrawCenter}
              onDrawLeft={handleDrawLeft}
              onDiscard={handleDiscard}
              onOpenHand={handleOpenHand}
              selectedTiles={selectedTiles}
              onTileSelect={handleTileSelect}
              turnTimeRemaining={turnTimeRemaining}
              localTiles={localTiles}
              onSortBySeries={handleSortBySeries}
              onSortByPairs={handleSortByPairs}
              onResetTiles={handleResetTiles}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
              dragOverIndex={dragOverIndex}
              onOpenSeries={handleOpenSeries}
              onOpenPairs={handleOpenPairs}
              smartHelpInfo={smartHelpInfo}
              selectedTilesValid={selectedTilesValid}
            />
          )
        ) : (
          // Bekleme odası
          <WaitingRoom
            room={room}
            currentUserId={user?.user_id}
            isCreator={isCreator}
            canStart={canStart}
            onReady={handleReady}
            onStart={handleStart}
          />
        )}
      </div>

      {/* Oyun İçi Chat */}
      {room?.status === "playing" && (
        <GameChat
          messages={chatMessages}
          onSendMessage={handleSendChatMessage}
          onSendReaction={handleSendReaction}
          currentUserId={user?.user_id}
          isMinimized={isChatMinimized}
          onToggleMinimize={() => setIsChatMinimized(!isChatMinimized)}
        />
      )}
    </div>
  );
}

// Bekleme odası komponenti
function WaitingRoom({ room, currentUserId, isCreator, canStart, onReady, onStart }) {
  const seats = [0, 1, 2, 3];
  const players = room?.players || [];
  const readyCount = players.filter(p => p.is_ready).length;
  const playerCount = players.length;

  const getPlayerAtSeat = (seatIndex) => {
    return players.find(p => p.seat_index === seatIndex);
  };

  return (
    <div className="h-full flex items-center justify-center p-8">
      <div className="max-w-2xl w-full">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-white mb-2">Oyuncular Bekleniyor</h2>
          <p className="text-white/50">
            {playerCount < 1 
              ? `En az 1 oyuncu gerekli (${playerCount}/1)`
              : `${readyCount}/${playerCount} oyuncu hazır`
            }
          </p>
        </div>

        {/* Masa görünümü */}
        <div className="relative w-80 h-80 mx-auto mb-8">
          {/* Masa */}
          <div className="absolute inset-8 rounded-full bg-gradient-to-br from-[#2d5a3d] to-[#1a472a] border-8 border-[#5C3317]" />

          {/* Koltuklar */}
          {seats.map((seatIndex) => {
            const player = getPlayerAtSeat(seatIndex);
            const positions = [
              "bottom-0 left-1/2 -translate-x-1/2 translate-y-4",
              "left-0 top-1/2 -translate-y-1/2 -translate-x-4",
              "top-0 left-1/2 -translate-x-1/2 -translate-y-4",
              "right-0 top-1/2 -translate-y-1/2 translate-x-4"
            ];

            return (
              <div
                key={seatIndex}
                className={cn("absolute", positions[seatIndex])}
              >
                <div className={cn(
                  "w-16 h-16 rounded-full flex flex-col items-center justify-center transition-all",
                  player 
                    ? player.is_ready 
                      ? "bg-green-500/20 border-2 border-green-500" 
                      : "bg-yellow-500/20 border-2 border-yellow-500"
                    : "bg-white/10 border-2 border-dashed border-white/30"
                )}>
                  {player ? (
                    <>
                      <span className="text-white font-bold text-lg">
                        {player.username?.[0]?.toUpperCase()}
                      </span>
                      {player.is_ready && (
                        <CheckCircle className="w-4 h-4 text-green-500 absolute -bottom-1" />
                      )}
                    </>
                  ) : (
                    <Users className="w-6 h-6 text-white/30" />
                  )}
                </div>
                {player && (
                  <div className="text-center mt-1">
                    <div className="text-white text-xs font-medium">
                      {player.user_id === currentUserId ? "Sen" : player.username}
                    </div>
                    <div className={cn(
                      "text-xs",
                      player.is_ready ? "text-green-400" : "text-yellow-400"
                    )}>
                      {player.is_ready ? "Hazır" : "Bekliyor"}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Kurallar */}
        <div className="bg-white/5 rounded-xl p-4 mb-6">
          <h3 className="text-white font-medium mb-3">Oyun Kuralları</h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-2 text-white/70">
              <span>Mod:</span>
              <span className="text-[#4DFFFF]">{room?.rules?.game_mode || "Tekli"}</span>
            </div>
            <div className="flex items-center gap-2 text-white/70">
              <span>Süre:</span>
              <span className="text-[#4DFFFF]">{room?.rules?.turn_time || 60}s</span>
            </div>
            <div className="flex items-center gap-2 text-white/70">
              <span>Katlamalı:</span>
              <span className={room?.rules?.is_doubling ? "text-green-400" : "text-red-400"}>
                {room?.rules?.is_doubling ? "Evet" : "Hayır"}
              </span>
            </div>
            <div className="flex items-center gap-2 text-white/70">
              <span>Tek Baraj:</span>
              <span className={room?.rules?.single_barrier ? "text-green-400" : "text-red-400"}>
                {room?.rules?.single_barrier ? "Evet" : "Hayır"}
              </span>
            </div>
          </div>
        </div>

        {/* Butonlar */}
        <div className="flex justify-center gap-4">
          <Button
            onClick={onReady}
            variant={players.find(p => p.user_id === currentUserId)?.is_ready ? "outline" : "default"}
            className={cn(
              "px-8",
              players.find(p => p.user_id === currentUserId)?.is_ready 
                ? "border-yellow-500 text-yellow-500" 
                : "bg-green-500 hover:bg-green-600"
            )}
          >
            {players.find(p => p.user_id === currentUserId)?.is_ready ? "Hazır Değilim" : "Hazırım"}
          </Button>

          {isCreator && (
            <Button
              onClick={onStart}
              disabled={!canStart}
              className="px-8 bg-[#4DFFFF] text-[#0B1622] hover:bg-[#3CEEEE] disabled:opacity-50"
            >
              <Play className="w-4 h-4 mr-2" />
              Oyunu Başlat
            </Button>
          )}
        </div>

        {isCreator && !canStart && (
          <p className="text-center text-white/50 text-sm mt-4">
            {players.length < 1 
              ? "En az 1 oyuncu gerekli"
              : "Tüm oyuncuların hazır olması gerekiyor"
            }
          </p>
        )}
      </div>
    </div>
  );
}
